import { useEffect, useState } from "react";
import axios from "../../AxiosInstance";
import { FaBook, FaUsers, FaBookReader, FaCalendarAlt } from "react-icons/fa";

const RecentluActivity = () => {
  const [activeTab, setActiveTab] = useState("bookClub");
  const [bookClubs, setBookClubs] = useState([]);
  const [bookDuos, setBookDuos] = useState([]);
  const [silentReadings, setSilentReadings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBookEvents = async (type) => {
    try {
      const response = await axios.get(`api/v1/books?type=${type}`);
      return response.data.data;
    } catch (error) {
      console.error(
        `Error fetching ${type}:`,
        error.response?.data?.message || error.message
      );
      throw error;
    }
  };

  const fetchAllEvents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [clubs, duos, readings] = await Promise.all([
        fetchBookEvents("Book Club"),
        fetchBookEvents("Book Duo"),
        fetchBookEvents("Silent Reading"),
      ]);
      setBookClubs(clubs);
      setBookDuos(duos);
      setSilentReadings(readings);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getEventDetails = (event) => ({
    title: event?.title,
    author: event?.author,
    date: `${event?.dateInfo?.day}/${event?.dateInfo?.month}/${event?.dateInfo?.year}`,
    time: `${event?.timeTrack?.minutes}:${event?.timeTrack?.seconds
      ?.toString()
      .padStart(2, "0")}`,
    attendees: event?.attendees?.length,
    type: event?.type,
  });

  useEffect(() => {
    fetchAllEvents();
  }, []);

  const renderTable = (events, type) => {
    const filteredEvents = events.filter((book) => book.attendees.length > 0);
    
    if (filteredEvents.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <p>No events with attendees found</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Event Name
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Attendees
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date Created
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredEvents.map((book) => (
              <tr key={book._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center gap-2">
                    {type === "bookClub" && <FaBook className="text-blue-500" />}
                    {type === "bookDuo" && <FaUsers className="text-green-500" />}
                    {type === "silentReading" && <FaBookReader className="text-purple-500" />}
                    <span className="truncate max-w-xs" title={book.title}>
                      {book.title.length > 30
                        ? book.title.substring(0, 30) + "..."
                        : book.title}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {book.attendees.length}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="text-gray-400" />
                    {getEventDetails(book)?.date}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const tabs = [
    { key: "bookClub", label: "Book Clubs", icon: FaBook, color: "blue" },
    { key: "bookDuo", label: "Book Duos", icon: FaUsers, color: "green" },
    { key: "silentReading", label: "Silent Readings", icon: FaBookReader, color: "purple" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FaCalendarAlt className="text-[#34c3c9] text-xl" />
          <h2 className="text-lg font-semibold text-gray-800">Recent Activity</h2>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex space-x-4" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            let activeClasses = "";
            if (isActive) {
              if (tab.color === "blue") {
                activeClasses = "border-blue-500 text-blue-600";
              } else if (tab.color === "green") {
                activeClasses = "border-green-500 text-green-600";
              } else if (tab.color === "purple") {
                activeClasses = "border-purple-500 text-purple-600";
              }
            }
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? activeClasses
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="text-base" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[200px]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#34c3c9] mb-2"></div>
              <p className="text-gray-500">Loading events...</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === "bookClub" && renderTable(bookClubs, "bookClub")}
            {activeTab === "bookDuo" && renderTable(bookDuos, "bookDuo")}
            {activeTab === "silentReading" && renderTable(silentReadings, "silentReading")}
          </>
        )}
      </div>
    </div>
  );
};

export default RecentluActivity;
